import { useCallback, useContext } from 'react';
import _ from 'lodash';
import { Call, CallVendor, PhoneContext, WebPhoneState } from './provider';
import { useKazoo } from '../use-kazoo';
import { now } from '../../utils/date-time';
import { entityCollection, getSortedCalls, shallowCompare } from '../../utils/collection';
import { Device, VoiceApi } from '../../services/api-client/voice-api';
import { usePhoneLock } from '../use-phone-lock/use-phone-lock';
import { findCall } from '../use-phone-actions/provider';


function selectDevice(devices: Device[]) {
    return _(devices)
        .filter(x => x.isWebRtc)
        .orderBy(x => x.kazooId)
        .first();
}
function mapCall(c: CallVendor, currentCallState: Call | undefined, now: number): Call {
    return {
        callId: c.callId,
        progress: c.established ? 'established' : c.ended ? 'ended' : 'initiating',
        onHold: c.held,
        primary: c.primary,
        inTransfer: c.inTransfer,
        direction: c.originating ? 'outbound' : 'inbound',
        remoteIdentity: c.remoteIdentityUser,
        initiated: currentCallState?.initiated ?? now,
        established: currentCallState?.established ?? (c.established ? now : undefined),
        isAudioMuted: c.isAudioMuted
    };
}
function isActive(call: Call) {
    return call.progress === 'established' && !call.onHold;
}

const handleCallUpdate = (hold: (callId: string) => void, calls: Call[], call?: Call, oldCall?: Call) => {
    if (!call || !oldCall) return;

    // if call is picked up or is un-held, need to put other calls on hold
    if (isActive(call) && !isActive(oldCall)) {
        calls.forEach(c => {
            if (c.callId !== call.callId && !c.onHold) {
                hold(c.callId);
            }
        });
    }
};

export default function usePhone() {
    const context = useContext(PhoneContext);
    const kazoo = useKazoo();
    const { tryLockDevice, checkActiveDeviceHasLock } = usePhoneLock();

    if (!context) throw new Error('usePhone must be used within a PhoneProvider');

    const refreshKazooDeviceSettings = useCallback(
        async (deviceId: string) => {
            const voiceApi = VoiceApi.getInstance();
            const kazooDeviceSettings = await voiceApi.getKazooDeviceSettings(deviceId);
            context.setIsWebPhoneEnabled(kazooDeviceSettings.enabled);
            context.setIsWebPhoneEnabledUI(kazooDeviceSettings.enabled);
        },
        [context]
    );

    const updateKazooDeviceSettings = useCallback(
        async (params: { deviceId: string; isEnabled: boolean }) => {
            const voiceApi = VoiceApi.getInstance();
            await voiceApi.updateKazooDeviceSettings(params.deviceId, params.isEnabled);
            context.setIsWebPhoneEnabled(params.isEnabled);
        },
        [context]
    );

    const lockDevice = async (force?: boolean) => {
        await tryLockDevice(context.deviceControl.deviceId!, context.clientId, force);
    }

    const confirmActiveDeviceHasLock = async (): Promise<void> => {
        checkActiveDeviceHasLock(context.deviceControl.deviceId!, context.clientId);
    }

    const init = useCallback(
        async (enableDevice?: boolean) => {
            if (context.deviceControl.deviceStatus === 'loading') return;

            context.setDeviceStatus('loading');
            const voiceApi = VoiceApi.getInstance();

            const kazooUser = kazoo.userInfo;
            if (!kazooUser) {
                context.setDeviceStatus('notconfigured');
                return;
            }

            const devices = await voiceApi.getDevices();
            const device = selectDevice(devices);

            if (!device) {
                context.setDeviceStatus('notcreated');
                return;
            }

            context.setDeviceId(device.kazooId);
            context.setDeviceStatus('ready');

            if (enableDevice) await updateKazooDeviceSettings({ deviceId: device.kazooId, isEnabled: true });

            await refreshKazooDeviceSettings(device.kazooId);
        },
        [context, kazoo.userInfo, refreshKazooDeviceSettings, lockDevice, updateKazooDeviceSettings]
    );

    const createDevice = useCallback(async () => {
        if (context.deviceControl.deviceStatus === 'creating') return;

        context.setDeviceStatus('creating');
        await VoiceApi.getInstance().createDevice();
        await init(true);
    }, [context, init]);

    const updatePhoneState = useCallback(
        async (webPhoneState: WebPhoneState) => {
            const webPhoneCalls = webPhoneState.calls.filter(c => c.hasSession && !c.ended);
            context.setCalls(c => {
              const currentCalls = c.allCalls;
              const ignoredCalls = webPhoneCalls.length > 0 ? c.ignoredCalls : {};
              const collapsedCalls = webPhoneCalls.length > 0 ? c.collapsedCalls : {};
              const calls = webPhoneCalls
                .map(c => mapCall(c, currentCalls[c.callId], now()))
                .filter(c => !ignoredCalls[c.callId]);
              const newCalls = entityCollection(calls, c => c.callId);
              // compare old and new state of each call to handle update callback on change
              const oldAndNewIds = _(newCalls.ids).concat(Object.keys(c.allCalls)).uniq();

              oldAndNewIds.forEach(id => {
                const currentCall = currentCalls[id];
                const newCall = newCalls.entities[id];
                if (!shallowCompare(currentCall, newCall)) {
                    handleCallUpdate((callId: string) => findCall(callId)?.hold(), calls, newCall, currentCall);
                }
              });
              const sortedCallsResp = getSortedCalls(calls, c => c.callId);
              
              return {
                allCalls: newCalls.entities, 
                ignoredCalls: ignoredCalls,
                collapsedCalls: collapsedCalls,
                sortedCalls: sortedCallsResp 
              };
            });
            context.setIsReady(webPhoneState.isReady);
        },
        [context.setIsReady, context.setCalls]
    );

    return {
        ...context,
        refreshKazooDeviceSettings,
        updateKazooDeviceSettings,
        init,
        createDevice,
        updatePhoneState,
        lockDevice,
        confirmActiveDeviceHasLock
    };
}

export function useCallSelector<T>(callId: string, selector: (call: Call) => T) {
    const { calls } = usePhone();

    const call = calls.allCalls[callId];

    if (!call) return undefined;

    return selector(call);
}
